import { useFeatureFlag } from '@app/lib/analytics/posthog';
import { ModelCatalogPicker } from '@core/component/AI/component/input/ModelCatalogPicker';
import { isLargeModelCatalog } from '@core/component/AI/component/input/modelCatalog';
import { MODEL_PRETTYNAME, Model } from '@core/component/AI/constant/model';
import { toast } from '@core/component/Toast/Toast';
import { claudeCloud } from '@core/constant/featureFlags';
import {
  MACRO_AGENT_BOT_ID,
  MACRO_HARNESS_NAME,
} from '@core/constant/macroAgent';
import { useChannelsContext } from '@core/context/channels';
import { useUserId } from '@core/context/user';
import { usePipedreamMcpFlag } from '@core/pipedream/flag';
import MacroLogo from '@icon/macro-logo.svg';
import PencilIcon from '@phosphor/pencil-simple.svg';
import PlusIcon from '@phosphor/plus.svg';
import AgentIcon from '@phosphor/sparkle.svg';
import TrashIcon from '@phosphor/trash.svg';
import UploadIcon from '@phosphor/upload-simple.svg';
import XIcon from '@phosphor/x.svg';
import {
  type AgentWithHarnessId,
  type CreateAgentParams,
  useAgentsQuery,
  useCreateAgentMutation,
  useDeleteAgentMutation,
  useUpdateAgentMutation,
} from '@queries/agents/agents';
import {
  type AgentModelTarget,
  buildAgentModelTargets,
  useAgentModelsQueries,
} from '@queries/agents/models';
import { useCursorApiKeyStatusQuery } from '@queries/auth/cursor-api-key';
import { useHarnessesQuery } from '@queries/harnesses/harnesses';
import { usePipedreamConnectedSlugs } from '@queries/pipedream-connectors';
import { useCurrentTeamQuery, useIsTeamOwner } from '@queries/team/teams';
import type { AgentMcpServer } from '@service-storage/generated/schemas/agentMcpServer';
import type { AgentMcpServers } from '@service-storage/generated/schemas/agentMcpServers';
import { useSearchParams } from '@solidjs/router';
import { Avatar, Button, Dialog, Panel } from '@ui';
import { createMemo, createSignal, For, Show } from 'solid-js';
import { botAssignableChannelOptions } from '../channel/Bots/botChannelOptions';
import { canDeleteBot, canManageAgent } from '../channel/Bots/botPermissions';
import { ChannelMultiSelect } from '../channel/Bots/ChannelMultiSelect';
import { PipedreamAppPicker } from './PipedreamAppPicker';
import {
  ChoiceRow,
  SettingsCard,
  SettingsPage,
  SettingsSection,
} from './primitives';

type AgentShare = 'Private' | 'Team';
type ChannelMode = 'all' | 'selected';

type AgentSummary = {
  id: string;
  name: string;
  tag: string;
  avatarUrl?: string;
  instructions: string;
  harness: string;
  defaultModel: string;
  channelSummary: string;
  share: AgentShare;
  persistedAgent?: AgentWithHarnessId;
  editable?: boolean;
};

type ConnectedHarness = {
  id: string;
  name: string;
  kind: 'builtin' | 'macrod';
  allowPermissionBypass: boolean;
  target: AgentModelTarget;
  connected?: boolean;
};

type ChannelOption = ReturnType<typeof botAssignableChannelOptions>[number];

const IN_MEMORY_HARNESS: ConnectedHarness = {
  id: 'in-memory',
  name: MACRO_HARNESS_NAME,
  kind: 'builtin',
  allowPermissionBypass: true,
  target: { harness: 'in-memory' },
};

const MACRO_AGENT: AgentSummary = {
  id: MACRO_AGENT_BOT_ID,
  name: 'Macro',
  tag: 'macro',
  instructions: '',
  harness: MACRO_HARNESS_NAME,
  defaultModel: MODEL_PRETTYNAME[Model.sonnet5],
  channelSummary: 'All channels',
  share: 'Team',
};

/** Settings page for viewing and creating persistent agents. */
export function Agents() {
  const claudeCloudFlag = useFeatureFlag(claudeCloud);
  const [creating, setCreating] = createSignal(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const creatingFromLink = () => searchParams.createAgent === 'true';
  const closeCreateAgent = () => {
    setCreating(false);
    if (creatingFromLink()) {
      setSearchParams({ createAgent: undefined }, { replace: true });
    }
  };
  const [editingAgent, setEditingAgent] = createSignal<AgentWithHarnessId>();
  const [deletingAgent, setDeletingAgent] = createSignal<AgentWithHarnessId>();
  const channelsContext = useChannelsContext();
  const currentUserId = useUserId();
  const agentsQuery = useAgentsQuery();
  const createAgentMutation = useCreateAgentMutation();
  const deleteAgentMutation = useDeleteAgentMutation();
  const updateAgentMutation = useUpdateAgentMutation();
  const currentTeamQuery = useCurrentTeamQuery();
  const isTeamOwner = useIsTeamOwner();
  const cursorStatus = useCursorApiKeyStatusQuery();
  const cursorConnected = () =>
    cursorStatus.isSuccess ? cursorStatus.data.registered : false;
  const harnessesQuery = useHarnessesQuery();
  const connectedHarnesses = (): readonly ConnectedHarness[] => {
    const harnesses = harnessesQuery.isSuccess ? harnessesQuery.data : [];
    return buildAgentModelTargets(
      cursorConnected(),
      harnesses,
      claudeCloudFlag().enabled
    ).map((target) => {
      if (target.harness === 'in-memory') return IN_MEMORY_HARNESS;
      if (target.harness === 'claude-cloud') {
        return {
          id: 'claude-cloud',
          name: 'Claude Cloud',
          kind: 'builtin',
          allowPermissionBypass: true,
          target,
        };
      }
      if (target.harness === 'cursor') {
        return {
          id: 'cursor',
          name: 'Cursor',
          kind: 'builtin',
          allowPermissionBypass: true,
          target,
        };
      }

      const harness = harnesses.find(
        (candidate) => candidate.id === target.harnessId
      );
      return {
        id: target.harnessId ?? '',
        name:
          harness?.owner.type === 'team'
            ? `${harness.name} · Team`
            : (harness?.name ?? 'macrod'),
        kind: 'macrod',
        allowPermissionBypass: harness?.allow_permission_bypass ?? false,
        target,
        connected: harness?.connected,
      };
    });
  };
  const channelOptions = createMemo(() =>
    botAssignableChannelOptions(channelsContext.channels())
  );
  const currentTeamId = () =>
    currentTeamQuery.isSuccess ? currentTeamQuery.data?.team.id : undefined;
  const canShareWithTeam = () => currentTeamId() !== undefined;
  const isAgentCreator = (agent: AgentWithHarnessId) =>
    agent.bot.created_by === currentUserId();
  const canMakePrivate = (agent: AgentWithHarnessId) =>
    agent.bot.owner?.type !== 'team' || isAgentCreator(agent);
  const canDeleteAgent = (agent: AgentWithHarnessId) =>
    canDeleteBot(agent.bot, currentUserId(), currentTeamId(), isTeamOwner());
  const agents = createMemo(() =>
    (agentsQuery.isSuccess ? agentsQuery.data : [])
      .filter((agent) =>
        canManageAgent(agent.bot, currentUserId(), currentTeamId())
      )
      .map((agent) =>
        summarizeAgent(agent, connectedHarnesses(), channelOptions())
      )
  );
  const teamAgents = createMemo(() => [
    MACRO_AGENT,
    ...agents().filter((agent) => agent.share === 'Team'),
  ]);
  const privateAgents = createMemo(() =>
    agents().filter((agent) => agent.share === 'Private')
  );

  const createAgent = async (agent: CreateAgentParams) => {
    try {
      await createAgentMutation.mutateAsync(agent);
      toast.success('Agent created');
      return true;
    } catch {
      toast.failure('Failed to create agent');
      return false;
    }
  };

  const updateAgent = async (agent: CreateAgentParams) => {
    const current = editingAgent();
    if (!current) return false;

    try {
      await updateAgentMutation.mutateAsync({
        ...agent,
        agentId: current.bot.id,
        ...(current.bot.description
          ? { description: current.bot.description }
          : {}),
      });
      toast.success('Agent updated');
      return true;
    } catch {
      toast.failure('Failed to update agent');
      return false;
    }
  };

  const deleteAgent = async () => {
    const current = deletingAgent();
    if (!current) return;

    try {
      await deleteAgentMutation.mutateAsync({
        agentId: current.bot.id,
        channelIds: current.channel_ids,
      });
      setDeletingAgent(undefined);
      toast.success('Agent deleted');
    } catch {
      toast.failure('Failed to delete agent');
    }
  };

  return (
    <>
      <SettingsPage
        title="Agents"
        description="Create agents with their own identity, instructions, and runtime."
        actions={
          <Button variant="cta" size="sm" onClick={() => setCreating(true)}>
            <PlusIcon />
            Create agent
          </Button>
        }
      >
        <SettingsSection
          title="Team agents"
          description="Agents shared with your team, including Macro."
        >
          <SettingsCard>
            <For each={teamAgents()}>
              {(agent) => (
                <AgentRow
                  agent={agent}
                  onEdit={
                    agent.editable && agent.persistedAgent
                      ? () => setEditingAgent(agent.persistedAgent)
                      : undefined
                  }
                  onDelete={
                    agent.persistedAgent && canDeleteAgent(agent.persistedAgent)
                      ? () => setDeletingAgent(agent.persistedAgent)
                      : undefined
                  }
                />
              )}
            </For>
          </SettingsCard>
        </SettingsSection>

        <SettingsSection
          title="Private agents"
          description="Agents owned by you rather than your team."
        >
          <SettingsCard>
            <Show
              when={privateAgents().length > 0}
              fallback={
                <p class="px-6 py-4 text-sm text-ink-muted">
                  {agentsQuery.isPending
                    ? 'Loading agents…'
                    : agentsQuery.isError
                      ? 'Your agents are unavailable.'
                      : 'No private agents yet.'}
                </p>
              }
            >
              <For each={privateAgents()}>
                {(agent) => (
                  <AgentRow
                    agent={agent}
                    onEdit={
                      agent.editable && agent.persistedAgent
                        ? () => setEditingAgent(agent.persistedAgent)
                        : undefined
                    }
                    onDelete={
                      agent.persistedAgent &&
                      canDeleteAgent(agent.persistedAgent)
                        ? () => setDeletingAgent(agent.persistedAgent)
                        : undefined
                    }
                  />
                )}
              </For>
            </Show>
            <Show when={agentsQuery.isError}>
              <p class="px-6 py-4 text-xs text-negative">
                Could not load your agents. Try refreshing this page.
              </p>
            </Show>
          </SettingsCard>
        </SettingsSection>
      </SettingsPage>

      <Show when={creating() || creatingFromLink()}>
        <AgentDialog
          connectedHarnesses={connectedHarnesses()}
          currentTeamId={currentTeamId()}
          canShareWithTeam={canShareWithTeam()}
          canMakePrivate
          pending={createAgentMutation.isPending}
          onClose={closeCreateAgent}
          onSave={createAgent}
        />
      </Show>
      <Show when={editingAgent()} keyed>
        {(agent) => (
          <AgentDialog
            agent={agent}
            connectedHarnesses={connectedHarnesses()}
            currentTeamId={currentTeamId()}
            canShareWithTeam={canShareWithTeam()}
            canMakePrivate={canMakePrivate(agent)}
            pending={updateAgentMutation.isPending}
            onClose={() => setEditingAgent(undefined)}
            onSave={updateAgent}
          />
        )}
      </Show>
      <Show when={deletingAgent()} keyed>
        {(agent) => (
          <AgentDeleteDialog
            agentName={agent.bot.name}
            pending={deleteAgentMutation.isPending}
            onClose={() => setDeletingAgent(undefined)}
            onConfirm={() => void deleteAgent()}
          />
        )}
      </Show>
    </>
  );
}

function summarizeAgent(
  agent: AgentWithHarnessId,
  harnesses: readonly ConnectedHarness[],
  channels: readonly ChannelOption[]
): AgentSummary {
  const harnessKey = agent.harness_id ?? agent.harness;
  const harness = harnesses.find((option) => option.id === harnessKey);
  const selectedChannelNames = channels
    .filter((channel) => agent.channel_ids.includes(channel.id))
    .map((channel) => `#${channel.name}`);
  const channelSummary =
    agent.channel_scope === 'all'
      ? 'All channels'
      : selectedChannelNames.length > 0
        ? selectedChannelNames.join(', ')
        : `${agent.channel_ids.length} selected ${agent.channel_ids.length === 1 ? 'channel' : 'channels'}`;

  return {
    id: agent.bot.id,
    name: agent.bot.name,
    tag: agent.bot.handle,
    avatarUrl: agent.bot.avatar_url ?? undefined,
    instructions: agent.instructions,
    harness: harness?.name ?? harnessName(harnessKey),
    defaultModel:
      MODEL_PRETTYNAME[agent.default_model as Model] ?? agent.default_model,
    channelSummary,
    share: agent.bot.owner?.type === 'team' ? 'Team' : 'Private',
    persistedAgent: agent,
    editable: true,
  };
}

function harnessName(id: string): string {
  if (id === 'in-memory') return MACRO_HARNESS_NAME;
  if (id === 'cursor') return 'Cursor';
  if (id === 'claude-cloud') return 'Claude Cloud';
  // Any other id is a registered macrod harness uuid; if it is not in the
  // connected list any more, the harness has been removed.
  return 'Disconnected harness';
}

function AgentRow(props: {
  agent: AgentSummary;
  onEdit?: () => void;
  onDelete?: () => void;
}) {
  return (
    <div class="flex items-center gap-4 px-6 py-4 mobile:items-start touch:px-4">
      <AgentAvatar agent={props.agent} />
      <div class="min-w-0 flex-1">
        <div class="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          <span class="truncate text-sm font-medium text-ink">
            {props.agent.name}
          </span>
          <span class="truncate text-xs text-ink-extra-muted">
            @{props.agent.tag}
          </span>
          <span class="shrink-0 rounded-full border border-edge-muted px-2 py-0.5 text-xxs font-medium uppercase text-ink-extra-muted">
            {props.agent.share}
          </span>
        </div>
        <p class="mt-0.5 text-xs text-ink-extra-muted">
          {props.agent.harness} · {props.agent.defaultModel} ·{' '}
          {props.agent.channelSummary}
        </p>
      </div>
      <div class="flex shrink-0 items-center gap-1">
        <Show when={props.onEdit}>
          {(onEdit) => (
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={`Edit ${props.agent.name}`}
              onClick={onEdit()}
            >
              <PencilIcon />
            </Button>
          )}
        </Show>
        <Show when={props.onDelete}>
          {(onDelete) => (
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              class="text-negative"
              aria-label={`Delete ${props.agent.name}`}
              onClick={onDelete()}
            >
              <TrashIcon />
            </Button>
          )}
        </Show>
      </div>
    </div>
  );
}

function AgentAvatar(props: { agent: AgentSummary }) {
  return (
    <Avatar size="lg" class="bg-surface text-accent ring ring-edge-muted">
      <Show
        when={props.agent.avatarUrl}
        fallback={
          <Avatar.Fallback>
            <Show
              when={props.agent.id === MACRO_AGENT_BOT_ID}
              fallback={<AgentIcon class="size-5" />}
            >
              <MacroLogo class="size-5" />
            </Show>
          </Avatar.Fallback>
        }
      >
        {(avatarUrl) => (
          <Avatar.Image src={avatarUrl()} alt={`${props.agent.name} avatar`} />
        )}
      </Show>
    </Avatar>
  );
}

function AgentDeleteDialog(props: {
  agentName: string;
  pending: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog
      open
      onOpenChange={(open) => !open && !props.pending && props.onClose()}
      position="center"
      visibleScrim
      class="w-[min(480px,calc(100vw-16px))]"
    >
      <Panel depth={2} class="rounded-xl text-ink">
        <Panel.Header class="px-5 py-3">
          <Dialog.Title class="text-sm font-semibold">
            Delete {props.agentName}?
          </Dialog.Title>
        </Panel.Header>
        <Panel.Body class="p-5">
          <Dialog.Description class="text-sm leading-5 text-ink-muted">
            This removes the agent from every channel and permanently deletes
            its configuration. This action cannot be undone.
          </Dialog.Description>
        </Panel.Body>
        <Panel.Footer class="justify-end gap-2 px-5 py-3">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={props.pending}
            onClick={props.onClose}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="danger"
            size="sm"
            disabled={props.pending}
            onClick={props.onConfirm}
          >
            <TrashIcon />
            {props.pending ? 'Deleting…' : 'Delete agent'}
          </Button>
        </Panel.Footer>
      </Panel>
    </Dialog>
  );
}

function AgentDialog(props: {
  agent?: AgentWithHarnessId;
  connectedHarnesses: readonly ConnectedHarness[];
  currentTeamId?: string;
  canShareWithTeam: boolean;
  canMakePrivate: boolean;
  pending: boolean;
  onClose: () => void;
  onSave: (agent: CreateAgentParams) => Promise<boolean>;
}) {
  const [name, setName] = createSignal(props.agent?.bot.name ?? '');
  const [tag, setTag] = createSignal(props.agent?.bot.handle ?? '');
  const [tagEdited, setTagEdited] = createSignal(props.agent !== undefined);
  const [avatarUrl, setAvatarUrl] = createSignal<string | undefined>(
    props.agent?.bot.avatar_url ?? undefined
  );
  const [instructions, setSystemPrompt] = createSignal(
    props.agent?.instructions ?? ''
  );
  const [harnessId, setHarnessId] = createSignal(
    props.agent?.harness_id ??
      props.agent?.harness ??
      props.connectedHarnesses[0]?.id ??
      ''
  );
  const modelQueries = useAgentModelsQueries(() =>
    props.connectedHarnesses.map((harness) => harness.target)
  );
  const selectedHarness = () =>
    props.connectedHarnesses.find((harness) => harness.id === harnessId());
  const modelQueryForHarness = (id: string) => {
    const index = props.connectedHarnesses.findIndex(
      (harness) => harness.id === id
    );
    return index >= 0 ? modelQueries[index] : undefined;
  };
  const modelDataForHarness = (id: string) => {
    const query = modelQueryForHarness(id);
    return query?.isSuccess ? query.data : undefined;
  };
  const preferredModelId = (id: string) => {
    const data = modelDataForHarness(id);
    if (data?.status === 'unsupported') return 'default';
    if (data?.status !== 'available') return '';
    const current = data.currentModel;
    if (
      current &&
      (data.models.length === 0 ||
        data.models.some((model) => model.id === current))
    ) {
      return current;
    }
    return data.models[0]?.id ?? '';
  };
  const [defaultModelId, setDefaultModelId] = createSignal(
    props.agent?.default_model ?? ''
  );
  const selectedDefaultModelId = () =>
    defaultModelId() || preferredModelId(harnessId());
  const selectedModelQuery = () => modelQueryForHarness(harnessId());
  const selectedModelData = () => modelDataForHarness(harnessId());
  const selectedModelOptions = () => {
    const data = selectedModelData();
    if (data?.status !== 'available') return [];

    const selected = selectedDefaultModelId();
    const savedModel =
      props.agent?.default_model === selected &&
      (props.agent.harness_id ?? props.agent.harness) === harnessId();
    if (
      selected.length === 0 ||
      data.models.some((model) => model.id === selected)
    ) {
      return data.models;
    }
    return [
      ...data.models,
      {
        id: selected,
        name: `${selected} (${savedModel ? 'saved, ' : ''}unavailable)`,
        description: undefined,
        group: undefined,
      },
    ];
  };
  const selectedCatalogOptions = () =>
    selectedModelOptions().map((model) => ({
      id: model.id,
      label: model.name,
      description: model.description ?? undefined,
      group: model.group ?? undefined,
    }));
  const selectedHarnessUsesCatalog = () =>
    isLargeModelCatalog(selectedCatalogOptions());
  const [channelMode, setChannelMode] = createSignal<ChannelMode>(
    props.agent?.channel_scope ?? 'all'
  );
  const [selectedChannelIds, setSelectedChannelIds] = createSignal<string[]>(
    props.agent?.channel_ids ?? []
  );
  const [share, setShare] = createSignal<AgentShare>(
    props.agent?.bot.owner?.type === 'team' ? 'Team' : 'Private'
  );
  const pipedreamMcp = usePipedreamMcpFlag();
  const connections = usePipedreamConnectedSlugs();
  const [mcp, setMcp] = createSignal<AgentMcpServers>(
    props.agent?.mcp ?? { scope: 'owner_connections' }
  );
  const selectedMcpServers = (): AgentMcpServer[] => {
    const current = mcp();
    return current.scope === 'selected' ? current.servers : [];
  };
  // Picks survive a round trip through "Use my connected apps", so toggling
  // the radio to compare does not throw the list away.
  let rememberedMcpServers: AgentMcpServer[] = selectedMcpServers();
  const setMcpScope = (scope: AgentMcpServers['scope']) => {
    if (scope === 'selected') {
      setMcp({ scope: 'selected', servers: rememberedMcpServers });
    } else {
      rememberedMcpServers = selectedMcpServers();
      setMcp({ scope: 'owner_connections' });
    }
  };
  const setSelectedMcpServers = (servers: AgentMcpServer[]) => {
    rememberedMcpServers = servers;
    setMcp({ scope: 'selected', servers });
  };
  const [autoAcceptChoice, setAutoAcceptChoice] = createSignal(
    props.agent?.auto_accept_permissions === true
  );
  const allowPermissionBypass = () =>
    selectedHarness()?.allowPermissionBypass === true;
  const autoAcceptPermissions = () =>
    selectedHarness()?.kind === 'builtin' ||
    (allowPermissionBypass() && autoAcceptChoice());
  let avatarInputRef: HTMLInputElement | undefined;
  let dialogContentRef: HTMLDivElement | undefined;

  const close = () => props.onClose();

  const handleNameInput = (value: string) => {
    setName(value);
    if (!tagEdited()) setTag(slugAgentTag(value));
  };

  const handleHarnessChange = (id: string) => {
    setHarnessId(id);
    setDefaultModelId(preferredModelId(id));
    setAutoAcceptChoice(false);
  };

  const handleAvatarInput = (file: File | undefined) => {
    if (!file) return;
    const reader = new FileReader();
    reader.addEventListener('load', () => {
      if (typeof reader.result === 'string') setAvatarUrl(reader.result);
    });
    reader.readAsDataURL(file);
  };

  const canCreate = () =>
    !props.pending &&
    name().trim().length > 0 &&
    tag().trim().length > 0 &&
    selectedHarness() !== undefined &&
    selectedDefaultModelId().length > 0 &&
    (channelMode() === 'all' || selectedChannelIds().length > 0) &&
    (mcp().scope === 'owner_connections' || selectedMcpServers().length > 0) &&
    (share() === 'Private' ? props.canMakePrivate : props.canShareWithTeam);

  const selectedTeamId = () => {
    if (share() === 'Private') return undefined;
    const currentOwner = props.agent?.bot.owner;
    return currentOwner?.type === 'team'
      ? currentOwner.team_id
      : props.currentTeamId;
  };

  const submit = async () => {
    if (!canCreate()) return;

    const harness = selectedHarness();
    const saved = await props.onSave({
      avatarUrl: avatarUrl(),
      channelIds: channelMode() === 'all' ? [] : selectedChannelIds(),
      channelScope: channelMode(),
      defaultModel: selectedDefaultModelId(),
      handle: slugAgentTag(tag()),
      // Registered macrod harnesses send the 'macrod' slug plus their uuid;
      // built-ins keep sending their own slug with no harness id.
      harness: harness?.kind === 'macrod' ? 'macrod' : (harness?.id ?? ''),
      harnessId: harness?.kind === 'macrod' ? harness.id : undefined,
      name: name().trim(),
      instructions: instructions().trim(),
      // Always sent, flag or no flag, so an editor without the Connections
      // section never wipes a selection somebody else made.
      mcp: mcp(),
      teamId: selectedTeamId(),
      autoAcceptPermissions: autoAcceptPermissions(),
    });
    if (saved) close();
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && close()}
      position="center"
      visibleScrim
      class="w-[min(720px,calc(100vw-16px))]"
      contentRef={(element) => {
        dialogContentRef = element;
      }}
    >
      <Panel depth={2} class="max-h-[88vh] rounded-xl text-ink">
        <Panel.Header class="justify-between px-3">
          <Dialog.Title as="span" class="m-0 p-0 text-sm font-medium">
            {props.agent ? 'Edit agent' : 'Create agent'}
          </Dialog.Title>
          <Dialog.CloseButton as={Button} variant="ghost" size="icon-sm">
            <XIcon />
          </Dialog.CloseButton>
        </Panel.Header>

        <Panel.Body class="overflow-y-auto p-5">
          <form
            id="agent-form"
            class="flex flex-col gap-6"
            onSubmit={(event) => {
              event.preventDefault();
              void submit();
            }}
          >
            <AgentFormSection
              title="Profile"
              description="How this agent appears in channels and mentions."
            >
              <div class="flex items-center gap-3 border-b border-edge-muted pb-4">
                <button
                  type="button"
                  aria-label="Upload avatar"
                  class="rounded-full outline-none focus-visible:ring-2 focus-visible:ring-accent"
                  onClick={() => avatarInputRef?.click()}
                >
                  <AgentAvatar
                    agent={{
                      id: 'draft',
                      name: name() || 'Agent',
                      tag: tag(),
                      avatarUrl: avatarUrl(),
                      instructions: '',
                      harness: '',
                      defaultModel: '',
                      channelSummary: '',
                      share: share(),
                    }}
                  />
                </button>
                <div class="min-w-0 flex-1">
                  <div class="text-sm font-medium text-ink">Avatar</div>
                  <div class="mt-0.5 text-xs text-ink-muted">
                    Optional · square images work best
                  </div>
                </div>
                <input
                  ref={avatarInputRef}
                  type="file"
                  accept="image/*"
                  class="hidden"
                  onChange={(event) =>
                    handleAvatarInput(event.currentTarget.files?.[0])
                  }
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => avatarInputRef?.click()}
                >
                  <UploadIcon />
                  Upload
                </Button>
              </div>

              <div class="mt-4 grid grid-cols-2 gap-3 mobile:grid-cols-1">
                <label class="flex flex-col gap-1.5">
                  <span class="text-xs font-medium text-ink">Name</span>
                  <input
                    autofocus
                    class="settings-input w-full"
                    placeholder="Bug fixer"
                    value={name()}
                    onInput={(event) =>
                      handleNameInput(event.currentTarget.value)
                    }
                  />
                </label>
                <label for="agent-tag" class="flex flex-col gap-1.5">
                  <span class="text-xs font-medium text-ink">@tag</span>
                  <div class="flex items-center rounded-lg border border-edge-muted px-2 focus-within:border-accent">
                    <span class="text-sm text-ink-extra-muted">@</span>
                    <input
                      id="agent-tag"
                      aria-label="@tag"
                      class="min-w-0 flex-1 bg-transparent px-1.5 py-2 text-sm text-ink outline-none"
                      placeholder="bug-fixer"
                      value={tag()}
                      onInput={(event) => {
                        setTagEdited(true);
                        setTag(slugAgentTag(event.currentTarget.value));
                      }}
                    />
                  </div>
                </label>
              </div>
            </AgentFormSection>

            <AgentFormSection
              title="Behavior"
              description="Instructions the agent receives at the start of every conversation."
            >
              <label class="flex flex-col gap-1.5">
                <span class="text-xs font-medium text-ink">System prompt</span>
                <textarea
                  rows={5}
                  class="settings-input h-auto min-h-30 w-full resize-y px-3 py-2.5 font-mono text-xs leading-5"
                  placeholder="You are a bug-fixing agent. Reproduce issues, identify root causes, and make focused, tested fixes…"
                  value={instructions()}
                  onInput={(event) =>
                    setSystemPrompt(event.currentTarget.value)
                  }
                />
              </label>
            </AgentFormSection>

            <AgentFormSection
              title="Runtime"
              description="Harnesses and models are limited to those currently connected."
            >
              <div class="grid grid-cols-2 gap-3 mobile:grid-cols-1">
                <label class="flex flex-col gap-1.5">
                  <span class="text-xs font-medium text-ink">Harness</span>
                  <select
                    class="settings-input w-full"
                    onChange={(event) =>
                      handleHarnessChange(event.currentTarget.value)
                    }
                  >
                    <For
                      each={props.connectedHarnesses.filter(
                        (harness) =>
                          harness.id !== 'claude-cloud' ||
                          harness.id === harnessId() ||
                          modelDataForHarness(harness.id)?.status ===
                            'available'
                      )}
                    >
                      {(harness) => (
                        <option
                          value={harness.id}
                          selected={harness.id === harnessId()}
                        >
                          {harness.name}
                        </option>
                      )}
                    </For>
                  </select>
                </label>
                <label class="flex flex-col gap-1.5">
                  <span class="text-xs font-medium text-ink">
                    Default model
                  </span>
                  <Show
                    when={selectedModelQuery()}
                    fallback={
                      <p class="settings-input text-ink-muted">
                        Model discovery unavailable
                      </p>
                    }
                    keyed
                  >
                    {(query) => (
                      <Show
                        when={!query.isPending}
                        fallback={
                          <select
                            aria-label="Default model"
                            class="settings-input w-full"
                            disabled
                          >
                            <option>Loading models…</option>
                          </select>
                        }
                      >
                        <Show
                          when={!query.isError}
                          fallback={
                            <div class="flex items-center gap-2">
                              <p class="min-w-0 flex-1 text-xs text-negative">
                                Could not load models for{' '}
                                {selectedHarness()?.name ?? 'this harness'}.
                              </p>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                aria-label={`Retry models for ${selectedHarness()?.name ?? 'this harness'}`}
                                onClick={() => void query.refetch()}
                              >
                                Retry
                              </Button>
                            </div>
                          }
                        >
                          <Show
                            when={selectedModelData()?.status === 'available'}
                            fallback={
                              <p class="settings-input text-ink-muted">
                                Model selection is unsupported by this harness.
                              </p>
                            }
                          >
                            <Show
                              when={selectedModelOptions().length > 0}
                              fallback={
                                <p class="settings-input text-ink-muted">
                                  This harness did not return any models.
                                </p>
                              }
                            >
                              <Show
                                when={selectedHarnessUsesCatalog()}
                                fallback={
                                  <select
                                    aria-label="Default model"
                                    class="settings-input w-full"
                                    value={selectedDefaultModelId()}
                                    onChange={(event) =>
                                      setDefaultModelId(
                                        event.currentTarget.value
                                      )
                                    }
                                  >
                                    <For each={selectedModelOptions()}>
                                      {(model) => (
                                        <option value={model.id}>
                                          {model.name}
                                        </option>
                                      )}
                                    </For>
                                  </select>
                                }
                              >
                                <ModelCatalogPicker
                                  value={selectedDefaultModelId()}
                                  options={selectedCatalogOptions()}
                                  onSelect={setDefaultModelId}
                                  ariaLabel="Default model"
                                  triggerClass="w-full justify-between"
                                  contentClass="overflow-hidden"
                                />
                              </Show>
                            </Show>
                          </Show>
                        </Show>
                      </Show>
                    )}
                  </Show>
                </label>
              </div>
              <Show when={selectedHarness()?.kind === 'macrod'}>
                <fieldset class="mt-4 grid gap-2 border-t border-ink/[0.06] pt-4">
                  <legend class="text-xs font-medium text-ink">
                    Permission requests
                  </legend>
                  <ChoiceRow
                    name="agent-permission-policy"
                    value="prompt"
                    title="Always prompt"
                    description="Session editors approve or reject each permission request."
                    checked={!autoAcceptPermissions()}
                    onChange={() => setAutoAcceptChoice(false)}
                  />
                  <Show
                    when={allowPermissionBypass()}
                    fallback={
                      <p class="text-xs text-ink-muted">
                        This harness requires permission prompts.
                      </p>
                    }
                  >
                    <ChoiceRow
                      name="agent-permission-policy"
                      value="bypass"
                      title="Always bypass"
                      description="Approve tool calls without asking."
                      checked={autoAcceptPermissions()}
                      onChange={() => setAutoAcceptChoice(true)}
                    />
                  </Show>
                </fieldset>
              </Show>
            </AgentFormSection>

            <Show when={pipedreamMcp()}>
              <AgentFormSection
                title="Connections"
                description="Which connected apps (MCP tools) this agent can use."
              >
                <fieldset class="flex flex-col gap-2">
                  <legend class="sr-only">Connections</legend>
                  <ChoiceRow
                    name="agent-mcp-mode"
                    value="owner_connections"
                    checked={mcp().scope === 'owner_connections'}
                    title="Use my connected apps"
                    description="The agent uses whatever apps the person running it has connected."
                    onChange={() => setMcpScope('owner_connections')}
                  />
                  <ChoiceRow
                    name="agent-mcp-mode"
                    value="selected"
                    checked={mcp().scope === 'selected'}
                    title="Specific apps"
                    description="Pick apps from the catalog. Each person connects their own account."
                    onChange={() => setMcpScope('selected')}
                  />
                </fieldset>

                <Show when={mcp().scope === 'selected'}>
                  <div class="mt-3 border-t border-edge-muted pt-3">
                    <PipedreamAppPicker
                      selected={selectedMcpServers()}
                      onChange={setSelectedMcpServers}
                      connectedSlugs={connections.slugs}
                      connectionsReady={connections.ready}
                      connectContainer={() => dialogContentRef}
                    />
                  </div>
                </Show>

                <Show when={share() === 'Team'}>
                  <p class="mt-3 border-t border-edge-muted pt-3 text-xs text-ink-extra-muted">
                    Connections are personal. Teammates who use this agent
                    connect these apps under Settings → Integrations; the
                    indicators here show only your own.
                  </p>
                </Show>
              </AgentFormSection>
            </Show>

            <AgentFormSection
              title="Channels"
              description="Choose whether this agent is global or channel-specific."
            >
              <fieldset class="flex flex-col gap-2">
                <legend class="sr-only">Channels</legend>
                <ChoiceRow
                  name="agent-channel-mode"
                  value="all"
                  checked={channelMode() === 'all'}
                  title="All channels"
                  description="The agent can be mentioned in every channel, like @macro."
                  onChange={() => setChannelMode('all')}
                />
                <ChoiceRow
                  name="agent-channel-mode"
                  value="selected"
                  checked={channelMode() === 'selected'}
                  title="Specific channels"
                  description="Only members of selected channels can use this agent."
                  onChange={() => setChannelMode('selected')}
                />
              </fieldset>

              <Show when={channelMode() === 'selected'}>
                <div class="mt-3 border-t border-edge-muted pt-3">
                  <ChannelMultiSelect
                    channelIds={selectedChannelIds()}
                    onChange={setSelectedChannelIds}
                  />
                </div>
              </Show>
            </AgentFormSection>

            <AgentFormSection
              title="Share"
              description="Choose who owns and can configure this agent."
            >
              <fieldset class="grid grid-cols-2 gap-2 mobile:grid-cols-1">
                <legend class="sr-only">Share</legend>
                <ChoiceRow
                  name="agent-share"
                  value="private"
                  checked={share() === 'Private'}
                  title="Private"
                  description={
                    props.canMakePrivate
                      ? 'Only you can use and manage this agent.'
                      : 'Only the agent creator can make it private.'
                  }
                  disabled={!props.canMakePrivate}
                  onChange={() => setShare('Private')}
                />
                <ChoiceRow
                  name="agent-share"
                  value="team"
                  checked={share() === 'Team'}
                  title="Team"
                  description={
                    props.canShareWithTeam
                      ? 'Your team can use this agent in shared channels.'
                      : 'Create or join a team before sharing agents.'
                  }
                  disabled={!props.canShareWithTeam}
                  onChange={() => setShare('Team')}
                />
              </fieldset>
              <Show when={!props.canShareWithTeam}>
                <p class="mt-3 border-t border-edge-muted pt-3 text-xs text-ink-extra-muted">
                  Team agents need a team owner. Create or join a team in Team
                  settings to enable this option.
                </p>
              </Show>
            </AgentFormSection>
          </form>
        </Panel.Body>

        <Panel.Footer class="justify-end gap-2 px-3 py-2">
          <Button type="button" variant="ghost" size="sm" onClick={close}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="agent-form"
            variant="cta"
            size="sm"
            disabled={!canCreate()}
          >
            {props.pending
              ? props.agent
                ? 'Saving…'
                : 'Creating…'
              : props.agent
                ? 'Save changes'
                : 'Create agent'}
          </Button>
        </Panel.Footer>
      </Panel>
    </Dialog>
  );
}

function AgentFormSection(props: {
  title: string;
  description: string;
  children: import('solid-js').JSX.Element;
}) {
  return (
    <section>
      <div class="mb-2 px-1">
        <h2 class="text-sm font-semibold text-ink">{props.title}</h2>
        <p class="mt-0.5 text-xs text-ink-muted">{props.description}</p>
      </div>
      <div class="rounded-xl border border-ink/[0.06] bg-surface-2 p-4">
        {props.children}
      </div>
    </section>
  );
}

function slugAgentTag(value: string): string {
  return value
    .toLowerCase()
    .replace(/^@/, '')
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}
